import React, {useMemo} from 'react';
import { useNavigate } from 'src/utils/use-navigate';
import {useStores} from '../../store';
import {getUiTextsByProtectionType, ProtectionType, UiTexts} from './deactivate-version.config';
import useCustomModal from '../custom-modal/custom-modal';
import usePromiseFeedback from '../promise-feedback/usePromiseFeedback';
import DeactivateIcon from '@svg/power.svg?react';
import ReactivateIcon from '@svg/reactivate.svg?react';
import {EventType} from '@src/dtos/analytics.dto';

export interface DeactivateVersionState {
    isDeactivated: boolean;
    uiTexts: UiTexts;
    deactivateReactivate: (nextDeactivate: boolean) => Promise<void>;
    pathToLatestVersion: string;
    isScheduledVersionUpcoming: boolean;
    icon: React.ReactElement;
    label: string;
    contextHolders: React.ReactElement;
    openModal: () => void;
}

export function useDeactivateVersion(protectionType: ProtectionType, closePanel?: () => void): DeactivateVersionState {
    const { fwVersionStore, customerStore, fwVersionBronzeStore } = useStores();
    const navigate = useNavigate();
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const [showModal, , contextHolder] = useCustomModal();

    const uiTexts = useMemo(() => getUiTextsByProtectionType(protectionType), [protectionType]);

    const isDeactivated =
        protectionType === 'DNS_PROTECTION'
            ? fwVersionBronzeStore.isDnsProtectionDeactivated
            : fwVersionStore.isVersionDeactivate;

    const deactivateReactivate = (nextDeactivate: boolean): Promise<void> => {
        if (protectionType === 'DNS_PROTECTION') {
            return fwVersionBronzeStore.deactivateReactivateDnsProtection(nextDeactivate);
        }
        return fwVersionStore.deactivateReactivateVersion(nextDeactivate);
    };

    const pathToLatestVersion = fwVersionStore.pathToLatestVersion;

    const openModal = (): void => {
        fwVersionStore.sendGeneralEvent(EventType.LinkClicked, {
            button: `${isDeactivated ? 'activate' : 'deactivate'}${uiTexts.featureName.replaceAll(' ', '')}`,
        });

        closePanel?.();
        showModal({
            width: 560,
            title: `${isDeactivated ? 'Reactivate' : 'Deactivate'} ${uiTexts.featureName}`,
            body: () => (
                <div className="modal-deactivation-version-message">
                    {isDeactivated ? uiTexts.reactivationBody : uiTexts.deactivationBody}
                </div>
            ),
            okText: isDeactivated ? 'Reactivate' : 'Yes, deactivate',
            cancelText: 'Cancel',
            onOk: () => {
                fwVersionStore.sendGeneralEvent(EventType.LinkClicked, {
                    button: isDeactivated ? 'Reactivate' : 'yesDeactivate',
                    location: isDeactivated
                        ? uiTexts.analyticsReactivationLocation
                        : uiTexts.analyticsDeactivationLocation,
                });
                promiseFeedbackFactory({
                    promise: deactivateReactivate(!isDeactivated),
                    notifObj: { show: false },
                    successCallback: () => {
                        closePanel?.();
                        navigate(pathToLatestVersion, {replace: true});
                    },
                });
            },
            onCancel: () => {
                fwVersionStore.sendGeneralEvent(EventType.LinkClicked, {
                    button: 'cancel',
                    location: isDeactivated
                        ? uiTexts.analyticsReactivationLocation
                        : uiTexts.analyticsDeactivationLocation,
                });
            },
        });
    };

    const icon = isDeactivated ? React.createElement(ReactivateIcon) : React.createElement(DeactivateIcon);
    const label = `${isDeactivated ? 'Reactivate' : 'Deactivate'} ${uiTexts.featureName}`;
    const contextHolders = React.createElement(React.Fragment, null, contextHolder, promiseContextHolder);

    return {
        isDeactivated,
        uiTexts,
        deactivateReactivate,
        pathToLatestVersion,
        isScheduledVersionUpcoming: customerStore.isScheduledVersionUpcoming,
        icon,
        label,
        contextHolders,
        openModal,
    };
}
